import { ReactNode, useMemo } from "react";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { theme } from "theme/theme";
import { DefaultRoutes } from "routes";
import { IntlProvider } from "react-intl";
import { messages } from "services/localizationService";
import { useAppIntl } from "services/useAppIntl";
import { useRecoilState } from "recoil";
import ReactGA from "react-ga4";
import { SnackbarMessage } from "utils/helpers/SnackbarMessage";
import { passwordChangedSuccess } from "state/userState";
import { AppContextProvider, useAppContext } from "context/AppContext";
import { FeatureFlagProvider, useFetchFeatureFlag } from "@STRSERVICE/optima-react-components";
import { fetchFeatureFlag, fetchFeatureFlags } from "services/featureFlagService";
import OfflineInfo from "layout/Components/OfflineInfo";
import { Maintenance } from "layout/maintenance";
import { useGlobalSnackbarMessage } from "hooks/useSnackbarMessage";
import { useIsAuthenticated } from "@azure/msal-react";
import SignalR from "layout/Components/SignalR/SignalR";

const App: React.FC = () => {
  ReactGA.initialize("G-B34WNCKLYE");
  const isAuthenticated = useIsAuthenticated();

  return (
    <ThemeProvider theme={theme("ltr")}>
      <AppContextProvider>
        <AppLanguage>
          <FeatureFlagProvider
            fetchFeatureFlag={fetchFeatureFlag}
            fetchFeatureFlags={fetchFeatureFlags}
            isAuthenticated={isAuthenticated}
          >
            <AppLayout />
            <OfflineInfo />
            <SignalR />
          </FeatureFlagProvider>
        </AppLanguage>
      </AppContextProvider>
    </ThemeProvider>
  );
};

export default App;

const AppLanguage = ({ children }: { children: ReactNode }) => {
  const { activeLanguage } = useAppContext();

  return (
    <IntlProvider defaultLocale="sv" locale={activeLanguage} messages={messages[activeLanguage]}>
      {children}
    </IntlProvider>
  );
};

const AppLayout = () => {
  const intl = useAppIntl();
  const { snackbarMessage } = useGlobalSnackbarMessage();
  const [passwordChanged, setPasswordChanged] = useRecoilState(passwordChangedSuccess);
  const setPasswordChangedMemo = useMemo(() => {
    return () => setPasswordChanged(false);
  }, [setPasswordChanged]);

  const maintenance = useFetchFeatureFlag("Maintenance");

  if (maintenance)
    return (
      <>
        <CssBaseline />
        <Maintenance />
      </>
    );

  return (
    <>
      <CssBaseline />
      <DefaultRoutes />
      <SnackbarMessage
        show={!!passwordChanged}
        type="success"
        message={intl.formatMessage({ id: "common.save.message" })}
        onClose={setPasswordChangedMemo}
      />
      {snackbarMessage}
    </>
  );
};
